import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import cx from "classnames";
import { easeInOut, motion, useTransform } from "framer-motion";
import { useContext } from "react";

// Types to determine animation behavior
type Transition = "in" | "out";
type Position = "left" | "middle" | "right";
type Row = "top" | "bottom";

/**
 * Builds the animation configurations for x and y positioning
 * @param transition
 * @param position
 * @param row
 * @returns
 */
const getAnimation = (transition?: Transition, position?: Position, row?: Row) => {
  // Default values
  const result = { x: [0, 0], y: [0, 0] };

  // Vertical transition
  result.y = row === "top" ? [-300, 0] : [300, 0];

  // Horizontal transition
  if (position === "left") {
    result.x = [-300, 0];
  }

  if (position === "right") {
    result.x = [300, 0];
  }

  // Flip for "out" transition
  if (transition === "out") {
    result.x.reverse();
    result.y.reverse();
  }

  return result;
};

const compileAnimationStyles = ({
  animationProgress,
  transition,
  position,
  row,
  delay,
}: {
  animationProgress: any;
  transition?: Transition;
  position?: Position;
  row?: Row;
  delay?: number;
}) => {
  // Adjust animation timing based on delay
  const inputRange = delay ? [0 + delay, 0.2 + delay] : [0, 0.2];

  // Obtain animation styles
  const animation = getAnimation(transition, position, row);
  const x = useTransform(animationProgress, inputRange, animation.x, { ease: easeInOut });
  const y = useTransform(animationProgress, inputRange, animation.y, { ease: easeInOut });
  const opacity = useTransform(animationProgress, inputRange, [0, 1], { ease: easeInOut });

  return { x, y, opacity };
};

export default function PictureFrame({
  src,
  label,
  className,
  transition,
  position,
  row,
  delay,
}: {
  src: string;
  label: string;
  className?: string;
  transition?: Transition;
  position?: Position;
  row?: Row;
  delay?: number;
}) {
  // Obtain scroll animation progress
  const animationProgress = useContext(ScrollAnimContext);

  // Obtain styles (animation) based on props
  const style = compileAnimationStyles({ animationProgress, transition, position, row, delay });

  return (
    <motion.div
      className={cx("p-2 xl:p-4 flex flex-col shadow-xl max-w-sm text-center bg-gray-200 text-gray-800 justify-between shadow-white/30", className)}
      style={style}
    >
      <img src={src} alt={label} className='object-cover h-full w-full' />
      {/* <div className='text-lg mt-4'>{label}</div> */}
    </motion.div>
  );
}
